import type { AccountMinimumViolation } from "@repo/shared/types";
import type { Localize } from "@/lib/i18n/use-localize";

/**
 * QL-362 — one display line per account-minimum violation. The template stays
 * in each surface's own translation table (`accountMinimumItem`, a function
 * key); only the value formatting is shared.
 */
export type AccountMinimumLine = { key: string; text: string };

/** The values an `accountMinimumItem` function key receives. */
export type AccountMinimumItemParams = {
  wrapper: string;
  value: string;
  currency: string;
  minimum: string;
  shortfall: string;
};

export function formatAccountMinimumLines(
  violations: AccountMinimumViolation[],
  template: (p: AccountMinimumItemParams) => string,
  localize: Localize,
  formatNumber: (value: number) => string,
): AccountMinimumLine[] {
  // A violation carries no account id, and two same-type source accounts at the
  // same value format identically — so the key is the account's wrapper +
  // portfolios, with an occurrence suffix should even those coincide.
  const seen = new Map<string, number>();
  return violations.map((v) => {
    const base = `${v.wrapperSyncKey}:${v.portfolioIds.join(",")}`;
    const occurrence = seen.get(base) ?? 0;
    seen.set(base, occurrence + 1);
    return {
      key: occurrence === 0 ? base : `${base}#${occurrence}`,
      text: template({
        wrapper: localize(v.wrapperNameI18n),
        value: formatNumber(v.postValue),
        minimum: formatNumber(v.minimum),
        shortfall: formatNumber(v.shortfall),
        currency: v.currency,
      }),
    };
  });
}
